import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { fmtMoney, fmtDate, statusMeta, todayInput } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileUploader } from "@/components/FileUploader";
import {
  ArrowLeft, ChevronRight, Plus, Trash2, Wallet, Receipt, CalendarDays,
  Hash, User2, StickyNote, TrendingUp, Building2, Camera, FileText, UploadCloud, Pencil
} from "lucide-react";

const PAYMENT_METHODS = [
  { v: "bank_transfer", l: "Bank Transfer" },
  { v: "credit_card",   l: "Credit Card" },
  { v: "cash",          l: "Cash" },
  { v: "cheque",        l: "Cheque" },
  { v: "other",         l: "Other" },
];

const emptyPayment = () => ({
  amount: "", payment_date: todayInput(), supplier: "",
  payment_method: "bank_transfer", invoice_number: "", notes: "",
});

export default function TradeDetail() {
  const { id } = useParams();
  const [trade, setTrade] = useState(null);
  const [payments, setPayments] = useState(null);
  const [openPay, setOpenPay] = useState(false);
  const [payForm, setPayForm] = useState(emptyPayment());
  const [pendingReceipt, setPendingReceipt] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", category: "", supplier: "", description: "", original_estimate: 0, updated_estimate: "", quoted_amount: "", approved_variations: 0, status: "not_quoted", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [tRes, pRes] = await Promise.all([
        api.get(`/trades/${id}`),
        api.get(`/trades/${id}/payments`),
      ]);
      setTrade(tRes.data);
      setPayments(pRes.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function addPayment(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...payForm,
        amount: Number(payForm.amount),
        payment_date: new Date(payForm.payment_date).toISOString(),
        supplier: payForm.supplier || trade?.supplier || "",
      };
      const { data: newPayment } = await api.post(`/trades/${id}/payments`, body);

      // If a receipt file was selected, upload it now
      if (pendingReceipt) {
        const fd = new FormData();
        fd.append("file", pendingReceipt);
        fd.append("kind", "receipt");
        fd.append("payment_id", newPayment.id);
        try {
          await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (uerr) {
          toast.error("Payment saved, but receipt upload failed: " + apiError(uerr));
        }
      }

      toast.success("Payment added");
      setOpenPay(false);
      setPayForm(emptyPayment());
      setPendingReceipt(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function removePayment(pid) {
    if (!window.confirm("Delete this payment?")) return;
    try {
      await api.delete(`/payments/${pid}`);
      toast.success("Payment removed");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  }

  if (!trade) return <div className="text-slate-500">Loading…</div>;
  const meta = statusMeta(trade.status);
  const f = trade.finance || {};

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2 text-sm">
        <Link to="/projects" className="text-slate-500 hover:text-brandblue" data-testid="link-back-projects">Projects</Link>
        <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
        <Link to={`/projects/${trade.project_id}`} className="text-slate-500 hover:text-brandblue inline-flex items-center gap-1" data-testid="link-back-project">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to project
        </Link>
      </div>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${meta.cls}`} data-testid="trade-detail-status">{meta.label}</span>
            {trade.category && <span className="text-xs uppercase tracking-widest text-slate-500">{trade.category}</span>}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight truncate" data-testid="trade-detail-title">{trade.name}</h1>
          {trade.supplier && (
            <div className="text-sm text-slate-500 mt-1.5 inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> {trade.supplier}</div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            className="h-11"
            onClick={() => { setEditForm({ name: trade.name || "", category: trade.category || "", supplier: trade.supplier || "", description: trade.description || "", original_estimate: trade.original_estimate ?? 0, updated_estimate: trade.updated_estimate ?? "", quoted_amount: trade.quoted_amount ?? "", approved_variations: trade.approved_variations ?? 0, status: trade.status || "not_quoted", notes: trade.notes || "" }); setOpenEdit(true); }}
            data-testid="btn-edit-trade"
          >
            <Pencil className="h-4 w-4" /> Edit trade
          </Button>
          <Dialog open={openPay} onOpenChange={(o) => { setOpenPay(o); if (!o) setPendingReceipt(null); }}>
          <DialogTrigger asChild>
            <Button className="h-12 bg-brandblue hover:bg-brandblue-hover text-white px-5" data-testid="btn-add-payment">
              <Plus className="h-4 w-4" /> Add payment
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-2xl">Record a partial payment</DialogTitle></DialogHeader>
            <form onSubmit={addPayment} className="space-y-4" data-testid="add-payment-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Amount (AUD)" required>
                  <Input type="number" step="0.01" min="0.01" required value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="h-12" data-testid="payment-amount" />
                </Field>
                <Field label="Payment date" required>
                  <Input type="date" required value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} className="h-12" data-testid="payment-date" />
                </Field>
              </div>
              <Field label="Supplier">
                <Input value={payForm.supplier} onChange={(e) => setPayForm({ ...payForm, supplier: e.target.value })} className="h-12" placeholder={trade.supplier || "Supplier name"} data-testid="payment-supplier" />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Payment method">
                  <Select value={payForm.payment_method} onValueChange={(v) => setPayForm({ ...payForm, payment_method: v })}>
                    <SelectTrigger className="h-12" data-testid="payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Invoice number">
                  <Input value={payForm.invoice_number} onChange={(e) => setPayForm({ ...payForm, invoice_number: e.target.value })} className="h-12" placeholder="INV-0001" data-testid="payment-invoice" />
                </Field>
              </div>
              <Field label="Notes">
                <Textarea value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} rows={2} data-testid="payment-notes" />
              </Field>
              <Field label="Receipt (optional)">
                <label className="flex items-center gap-3 h-12 px-3 border border-input rounded-md cursor-pointer hover:bg-slate-50 transition-colors" data-testid="payment-receipt-picker">
                  <UploadCloud className="h-4 w-4 text-slate-500" />
                  <span className="text-sm text-slate-600 truncate">{pendingReceipt ? pendingReceipt.name : "Attach receipt (PDF or image)"}</span>
                  <input type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => setPendingReceipt(e.target.files?.[0] || null)} data-testid="payment-receipt-input" />
                </label>
              </Field>
              <DialogFooter>
                <Button type="submit" disabled={saving} className="h-12 bg-brandblue hover:bg-brandblue-hover text-white" data-testid="btn-save-payment">
                  {saving ? "Saving…" : "Record payment"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">Edit trade</DialogTitle></DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            setSaving(true);
            try {
              const body = {
                ...editForm,
                original_estimate: Number(editForm.original_estimate) || 0,
                updated_estimate: editForm.updated_estimate === "" ? null : Number(editForm.updated_estimate),
                quoted_amount: editForm.quoted_amount === "" ? null : Number(editForm.quoted_amount),
                approved_variations: Number(editForm.approved_variations) || 0,
              };
              await api.put(`/trades/${id}`, body);
              toast.success("Trade updated");
              setOpenEdit(false);
              load();
            } catch (err) { toast.error(apiError(err)); }
            finally { setSaving(false); }
          }} className="space-y-4" data-testid="edit-trade-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Trade name" required>
                <Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="h-12" data-testid="edit-trade-name" />
              </Field>
              <Field label="Category">
                <Input value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} className="h-12" data-testid="edit-trade-category" />
              </Field>
            </div>
            <Field label="Supplier">
              <Input value={editForm.supplier} onChange={(e) => setEditForm({ ...editForm, supplier: e.target.value })} className="h-12" data-testid="edit-trade-supplier" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Original estimate">
                <Input type="number" min="0" value={editForm.original_estimate} onChange={(e) => setEditForm({ ...editForm, original_estimate: e.target.value })} className="h-12" data-testid="edit-trade-original" />
              </Field>
              <Field label="Updated estimate">
                <Input type="number" min="0" value={editForm.updated_estimate} onChange={(e) => setEditForm({ ...editForm, updated_estimate: e.target.value })} className="h-12" data-testid="edit-trade-updated" />
              </Field>
              <Field label="Quoted amount">
                <Input type="number" min="0" value={editForm.quoted_amount} onChange={(e) => setEditForm({ ...editForm, quoted_amount: e.target.value })} className="h-12" data-testid="edit-trade-quoted" />
              </Field>
              <Field label="Approved variations">
                <Input type="number" value={editForm.approved_variations} onChange={(e) => setEditForm({ ...editForm, approved_variations: e.target.value })} className="h-12" data-testid="edit-trade-variations" />
              </Field>
            </div>
            <Field label="Status">
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger className="h-12" data-testid="edit-trade-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_quoted">Not Quoted</SelectItem>
                  <SelectItem value="quoted">Quoted</SelectItem>
                  <SelectItem value="not_started">Not Started</SelectItem>
                  <SelectItem value="part_paid">Part Paid</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="over_budget">Over Budget</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Notes">
              <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} data-testid="edit-trade-notes" />
            </Field>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="h-12 bg-brandblue hover:bg-brandblue-hover text-white" data-testid="btn-save-edit-trade">
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Trade financials */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger" data-testid="trade-finance-grid">
        <Metric label="Original estimate" value={fmtMoney(f.original_estimate)} />
        <Metric label="Updated estimate" value={fmtMoney(f.updated_estimate)} />
        <Metric label="Quoted amount" value={fmtMoney(f.quoted_amount)} />
        <Metric label="Approved variations" value={fmtMoney(f.approved_variations)} />
        <Metric label="Estimated final" value={fmtMoney(f.estimated_final)} icon={<TrendingUp />} />
        <Metric label="Total paid" value={fmtMoney(f.total_paid, true)} tone="paid" icon={<Wallet />} />
        <Metric label="Remaining balance" value={fmtMoney(f.remaining_balance, true)} tone={f.remaining_balance > 0 ? "part" : "paid"} icon={<Receipt />} />
        <Metric label="Payments recorded" value={String(trade.payment_count || 0)} />
      </section>

      {trade.notes && (
        <Card className="p-5 border border-border shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
            <StickyNote className="h-3.5 w-3.5" /> Notes
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{trade.notes}</p>
        </Card>
      )}

      {/* Invoices */}
      <Card className="p-5 border border-border shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-4 w-4 text-slate-500" />
          <h3 className="font-display text-lg font-semibold text-slate-900">Invoices</h3>
        </div>
        <FileUploader
          kind="invoice"
          tradeId={id}
          label="Supplier invoices (PDF or image)"
          accept="image/*,application/pdf"
        />
      </Card>

      {/* Photos */}
      <Card className="p-5 border border-border shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Camera className="h-4 w-4 text-slate-500" />
          <h3 className="font-display text-lg font-semibold text-slate-900">Site photos</h3>
        </div>
        <FileUploader
          kind="photo"
          tradeId={id}
          label="Photos"
          accept="image/*"
        />
      </Card>

      {/* Payment history */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Payment history</div>
            <h2 className="font-display text-2xl font-semibold text-slate-900 mt-1">{payments?.length || 0} payments</h2>
          </div>
        </div>
        {payments === null ? (
          <div className="h-24 bg-white border border-border rounded-lg animate-pulse" />
        ) : payments.length === 0 ? (
          <Card className="p-10 text-center border-dashed border-2" data-testid="payments-empty">
            <Wallet className="h-8 w-8 text-slate-400 mx-auto mb-3" />
            <h3 className="font-display text-lg font-semibold">No payments yet</h3>
            <p className="text-sm text-slate-500 mt-1">Record partial payments to keep your running balance accurate.</p>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="payments-list">
            {payments.map((p) => <PaymentRow key={p.id} p={p} onDelete={() => removePayment(p.id)} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function PaymentRow({ p, onDelete }) {
  return (
    <Card className="border border-border shadow-sm p-4 md:p-5" data-testid={`payment-row-${p.id}`}>
      <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-status-paid-bg text-status-paid-text flex items-center justify-center shrink-0">
              <Wallet className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold text-lg text-slate-900" data-testid={`payment-amount-${p.id}`}>{fmtMoney(p.amount, true)}</div>
              <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {fmtDate(p.payment_date)}</span>
                {p.supplier && <span className="inline-flex items-center gap-1"><User2 className="h-3 w-3" /> {p.supplier}</span>}
                {p.invoice_number && <span className="inline-flex items-center gap-1"><Hash className="h-3 w-3" /> {p.invoice_number}</span>}
                <span className="uppercase tracking-wider">{(p.payment_method || "").replace("_", " ")}</span>
              </div>
              {p.notes && <div className="text-xs text-slate-600 mt-1 line-clamp-2">{p.notes}</div>}
            </div>
          </div>
          {/* Receipt attachments for this payment */}
          <div className="mt-3 pl-13">
            <FileUploader kind="receipt" paymentId={p.id} label="Receipt" accept="image/*,application/pdf" />
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onDelete} className="h-9 w-9 text-slate-400 hover:text-status-danger-text self-end md:self-auto" data-testid={`btn-delete-payment-${p.id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
}

function Metric({ icon, label, value, tone = "neutral" }) {
  const toneCls = { neutral: "text-slate-900", paid: "text-status-paid-text", part: "text-status-part-text", danger: "text-status-danger-text" }[tone];
  return (
    <Card className="p-4 border border-border shadow-sm">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
        {icon && <span className="[&>svg]:h-3.5 [&>svg]:w-3.5 text-slate-400">{icon}</span>}
        {label}
      </div>
      <div className={`font-display font-bold text-xl md:text-2xl mt-1 ${toneCls}`}>{value}</div>
    </Card>
  );
}

function Field({ label, children, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
        {label} {required && <span className="text-status-danger-text">*</span>}
      </Label>
      {children}
    </div>
  );
}
